import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout, { StatusDot } from "@/components/Layout";
import { ScoreGauge, StatusBadge, NationBadge } from "@/components/Compliance";
import { NATION_META } from "@/lib/nations";
import { AlertTriangle, Building2, Mail, Copy, CalendarClock, Plus, Inbox } from "lucide-react";
import { toast } from "sonner";
import DepositAlert from "@/components/DepositAlert";
import LawRadar from "@/components/LawRadar";
import Assistant from "@/components/Assistant";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const emailInRef = useRef();

  const load = () => api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const testEmailIn = async (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("subject", f.name);
    fd.append("file", f);
    toast.loading("Receiving email & auto-filing…", { id: "emailin" });
    try {
      const { data: r } = await api.post("/inbound/simulate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.ok && r.filed > 0) toast.success(`Filed ${r.filed} certificate to "${r.property}"`, { id: "emailin" });
      else if (r.reason === "no_property") toast.error("Add a property first", { id: "emailin" });
      else toast.warning("Received, but nothing could be filed", { id: "emailin" });
      load();
    } catch { toast.error("Email-in failed", { id: "emailin" }); }
  };

  const trialDays = user?.trial_ends_at ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / 86400000)) : null;

  if (!data) return <Layout><div className="py-20 text-center text-muted-foreground">Loading dashboard…</div></Layout>;

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Portfolio dashboard</h1>
          <p className="text-muted-foreground text-sm mt-1">{user?.nations?.map((n) => NATION_META[n]?.lawLabel).join(" · ")}</p>
        </div>
        <Link to="/properties" data-testid="add-property-cta" className="min-h-[44px] px-4 inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-brand hover:bg-emerald-deep text-white font-semibold transition-colors"><Plus size={18} /> Add property</Link>
      </div>

      {user?.billing_status === "past_due" && (
        <div data-testid="past-due-banner" className="mb-6 flex items-center gap-3 p-4 rounded-2xl bg-rose-600 text-white">
          <AlertTriangle size={20} />
          <p className="text-sm flex-1"><span className="font-semibold">Your last payment failed.</span> Update your card to keep reminders running.</p>
          <Link to="/settings" className="text-sm font-semibold underline">Fix now</Link>
        </div>
      )}
      {trialDays !== null && user?.plan === "trial" && (
        <div data-testid="trial-banner" className="mb-6 flex items-center gap-3 p-4 rounded-2xl bg-navy text-white">
          <CalendarClock size={20} className="text-emerald-glow" />
          <p className="text-sm flex-1"><span className="font-semibold">{trialDays} days left</span> on your free trial. Add a plan anytime — no interruption.</p>
          <Link to="/settings" className="text-sm font-semibold underline">Manage</Link>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Score card */}
        <div className="bg-card border border-border rounded-2xl p-6 flex items-center gap-6">
          <ScoreGauge score={data.portfolio_score} />
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Overall compliance</p>
            <div className="mt-2 space-y-1.5 text-sm">
              <div className="flex items-center gap-2"><StatusDot status="green" /> {data.counts.green} compliant</div>
              <div className="flex items-center gap-2"><StatusDot status="amber" /> {data.counts.amber} due soon</div>
              <div className="flex items-center gap-2"><StatusDot status="red" /> {data.counts.red} need action</div>
            </div>
          </div>
        </div>

        {/* Registrations */}
        <div className="bg-card border border-border rounded-2xl p-6 lg:col-span-2">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">Your registrations</p>
          {data.registrations.length === 0 && <p className="text-sm text-muted-foreground">No nations configured.</p>}
          <div className="grid sm:grid-cols-2 gap-3">
            {data.registrations.map((r) => (
              <div key={r.nation} data-testid={`reg-${r.nation.toLowerCase()}`} className="flex items-center gap-3 p-3 rounded-xl border border-border">
                <StatusDot status={r.status} className="h-3 w-3" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{r.label}</div>
                  <div className="text-xs text-muted-foreground truncate">{r.number ? `${r.number} · ` : ""}{r.detail}</div>
                </div>
                <NationBadge nation={r.nation} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <DepositAlert alerts={data.deposit_alerts} />

      {/* Email-in */}
      <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-2xl bg-emerald-50 border border-emerald-200">
        <Mail className="text-emerald-brand" size={20} />
        <div className="flex-1">
          <p className="text-sm font-semibold text-emerald-900">Your email-in address</p>
          <p className="text-xs text-emerald-800">Forward any certificate here and it files itself to the right property.</p>
        </div>
        <button data-testid="copy-email-in" onClick={() => { navigator.clipboard?.writeText(user?.forwarding_email || ""); toast.success("Copied"); }}
          className="inline-flex items-center gap-2 px-3 min-h-[40px] rounded-lg bg-white border border-emerald-200 font-mono text-xs text-emerald-900">
          {user?.forwarding_email} <Copy size={14} />
        </button>
        <input ref={emailInRef} type="file" accept="image/*,application/pdf" className="hidden" data-testid="emailin-file" onChange={(e) => { testEmailIn(e.target.files[0]); e.target.value = ""; }} />
        <button data-testid="test-email-in" onClick={() => emailInRef.current?.click()}
          className="inline-flex items-center gap-2 px-3 min-h-[40px] rounded-lg bg-emerald-brand hover:bg-emerald-deep text-white text-xs font-semibold transition-colors">
          <Inbox size={14} /> Test email-in
        </button>
      </div>

      {/* Actions this week */}
      <div className="mt-8">
        <h2 className="font-heading text-xl font-bold flex items-center gap-2"><AlertTriangle size={20} className="text-amber-500" /> Needs action this week</h2>
        {data.actions.length === 0 ? (
          <div className="mt-3 p-6 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">🎉 All caught up — nothing needs attention right now.</div>
        ) : (
          <div className="mt-3 space-y-2">
            {data.actions.slice(0, 8).map((a, i) => (
              <Link key={i} to={`/properties/${a.property_id}`} data-testid={`action-${i}`} className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border hover:shadow-md transition-shadow">
                <StatusDot status={a.status} className="h-3 w-3" />
                <div className="flex-1 min-w-0"><div className="font-semibold text-sm truncate">{a.rule}</div><div className="text-xs text-muted-foreground truncate">{a.property_name} · {a.label}</div></div>
                <StatusBadge status={a.status} />
              </Link>
            ))}
          </div>
        )}
      </div>

      {data.upcoming?.length > 0 && (
        <div className="mt-8" data-testid="upcoming-renewals">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2"><CalendarClock size={20} className="text-navy" /> Upcoming renewals</h2>
          <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.upcoming.map((u) => (
              <Link key={u.key} to={u.property_id ? `/properties/${u.property_id}` : "/settings"} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-card border border-border text-sm hover:shadow-md transition-shadow">
                <div className="min-w-0"><div className="font-medium truncate">{u.title}</div><div className="text-xs text-muted-foreground truncate">{u.property_name} · {u.date}</div></div>
                <span className={`text-xs font-semibold whitespace-nowrap ${u.days_left < 0 ? "text-rose-600" : u.days_left <= 30 ? "text-amber-600" : "text-emerald-deep"}`}>{u.days_left < 0 ? `${-u.days_left}d overdue` : u.days_left === 0 ? "today" : `${u.days_left}d`}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <LawRadar />
      <Assistant />

      {/* Properties grid */}
      <div className="mt-8">
        <h2 className="font-heading text-xl font-bold">Your properties</h2>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.properties.map((p) => (
            <Link key={p.id} to={`/properties/${p.id}`} data-testid={`prop-card-${p.id}`} className="bg-card border border-border rounded-2xl p-5 hover:shadow-xl hover:-translate-y-0.5 transition-all">
              <div className="flex items-start justify-between gap-2">
                <div className="h-10 w-10 grid place-items-center rounded-lg bg-secondary"><Building2 size={18} className="text-navy" /></div>
                <NationBadge nation={p.nation} />
              </div>
              <div className="mt-3 font-semibold">{p.name}</div>
              <div className="text-sm text-muted-foreground truncate">{p.address}</div>
              <div className="mt-4 flex items-center justify-between">
                <StatusBadge status={p.status} />
                <span className="font-heading font-extrabold text-lg">{p.score}<span className="text-xs text-muted-foreground">/100</span></span>
              </div>
            </Link>
          ))}
          {data.properties.length === 0 && <div className="text-sm text-muted-foreground">No properties yet.</div>}
        </div>
      </div>
    </Layout>
  );
}
